/**
 * Shared hook for asset deletion with confirmation dialog.
 * Used by both useAssets (single-project context) and SearchResultsList (multi-project context).
 * Handles confirmation UI, API call, and toast notifications.
 * Callers handle context-specific cache invalidation via onSuccess callback.
 */

import { useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { deleteAsset as deleteAssetApi } from "@/services/api/sdk.gen";
import { useConfirmationDialog } from "./useConfirmationDialog";
import { AssetFilename } from "@/components/assets/AssetFilename";
import { showErrorToast } from "@/utils/showErrorToast";
import type { Asset } from "@/types/asset.types";

interface DeleteAssetOptions {
  /** Additional cache invalidation after successful delete */
  onSuccess?: () => void;
  /** Preserve rejection-based error handling for useAssets callers. */
  throwOnError?: boolean;
}

/**
 * Hook providing asset deletion with i18n confirmation dialog.
 * Invalidates standard asset query families; callers can add more via onSuccess.
 */
export function useAssetDelete() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { confirm } = useConfirmationDialog();
  const [isDeleting, setIsDeleting] = useState(false);

  const deleteAsset = useCallback(
    async (
      asset: Asset,
      projectId: string | undefined,
      options?: DeleteAssetOptions,
    ): Promise<boolean> => {
      const confirmed = await confirm({
        title: (
          <span className="flex items-center gap-2">
            {t("assets.deleteConfirm.title")}{" "}
            <AssetFilename fileName={asset.filename} maxWidth="max-w-[300px]" />
            ?
          </span>
        ),
        message: t("assets.deleteConfirm.message"),
        confirmText: t("assets.deleteConfirm.confirm"),
        cancelText: t("assets.deleteConfirm.cancel"),
        variant: "destructive",
      });

      if (!confirmed) return false;

      setIsDeleting(true);
      try {
        await deleteAssetApi({
          path: { assetId: asset.id },
          throwOnError: true,
        });

        // Invalidate standard asset query families
        if (projectId) {
          queryClient.invalidateQueries({ queryKey: ["assets", projectId] });
          queryClient.invalidateQueries({
            queryKey: ["assets", "infinite", projectId],
          });
        }
        // Refresh entitlement counters
        queryClient.invalidateQueries({ queryKey: ["projects"] });

        // Allow caller to add context-specific invalidation
        options?.onSuccess?.();

        toast.success(t("assets.toast.deleteSuccess"));
        return true;
      } catch (error) {
        showErrorToast({
          message: t("assets.toast.deleteError"),
          error,
          projectId,
        });
        if (options?.throwOnError) throw error;
        return false;
      } finally {
        setIsDeleting(false);
      }
    },
    [confirm, queryClient, t],
  );

  return { deleteAsset, isDeleting };
}
